/**
 * Cozinha explícita pedida pelo utilizador (módulo puro — sem IA, sem Maps).
 *
 * Quando o pedido nomeia uma cozinha ("restaurantes japoneses em Leça da
 * Palmeira"), essa cozinha passa a ser CONDIÇÃO VINCULATIVA: nunca pode haver
 * substituição silenciosa por mercados, food halls ou restaurantes genéricos.
 *
 * A cozinha original do pedido é sempre preservada, mesmo quando não existe no
 * catálogo de aliases: nesse caso continua vinculativa e a validação faz-se
 * pelos tipos do Places e, em último caso, por prova no nome.
 */

export type CuisineIntent = {
  /** Termo tal como foi pedido ("japoneses", "libanesa"). */
  requested: string;
  /** Id canónico quando conhecido ("japanese"); null preserva o pedido tal e qual. */
  id: string | null;
  /** Rótulo em inglês para a pesquisa do Places ("japanese"). */
  searchTerm: string;
  /** Tipos do Places que provam a cozinha (primaryType/types). */
  placeTypes: string[];
  /** Palavras que provam a cozinha no nome do estabelecimento. */
  nameHints: string[];
  /** A cozinha é sempre obrigatória quando detetada. */
  strict: true;
};

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

type CuisineEntry = {
  id: string;
  searchTerm: string;
  re: RegExp;
  placeTypes: string[];
  nameHints: string[];
};

const CUISINES: CuisineEntry[] = [
  {
    id: "japanese",
    searchTerm: "japanese",
    re: /\b(japones\w*|japonais\w*|japanese|japones|japon\w*|giappones\w*|japanisch\w*|sushi|ramen|izakaya|teppanyaki|yakitori)\b/u,
    placeTypes: [
      "japanese_restaurant",
      "sushi_restaurant",
      "ramen_restaurant",
      "japanese_curry_restaurant",
      "asian_restaurant",
    ],
    nameHints: ["sushi", "ramen", "izakaya", "japones", "japanese", "sake", "teppanyaki", "yakitori", "wasabi", "tokyo", "kyoto", "nikkei"],
  },
  {
    id: "italian",
    searchTerm: "italian",
    re: /\b(italian\w*|italian[oa]s?|italien\w*|italienisch\w*|trattoria|osteria|pizzari\w*)\b/u,
    placeTypes: ["italian_restaurant", "pizza_restaurant"],
    nameHints: ["italian", "italian[oa]", "trattoria", "osteria", "pizzeria", "pizza", "pasta"],
  },
  {
    id: "chinese",
    searchTerm: "chinese",
    re: /\b(chines\w*|chinese|chino\w*|chinois\w*|cinese|chinesisch\w*|dim\s*sum)\b/u,
    placeTypes: ["chinese_restaurant", "asian_restaurant"],
    nameHints: ["china", "chines", "chinese", "wok", "dim sum", "mandarim", "shanghai", "pequim"],
  },
  {
    id: "indian",
    searchTerm: "indian",
    re: /\b(indian\w*|indian[oa]s?|indien\w*|indisch\w*|tandoori|curry\s+indian\w*)\b/u,
    placeTypes: ["indian_restaurant", "asian_restaurant"],
    nameHints: ["india", "indian", "tandoori", "masala", "curry", "punjab", "bombay", "delhi"],
  },
  {
    id: "thai",
    searchTerm: "thai",
    re: /\b(tailandes\w*|thai\w*|thail[aä]ndisch\w*|tailandais\w*)\b/u,
    placeTypes: ["thai_restaurant", "asian_restaurant"],
    nameHints: ["thai", "tailand", "bangkok", "pad thai"],
  },
  {
    id: "mexican",
    searchTerm: "mexican",
    re: /\b(mexican\w*|mexican[oa]s?|mejican\w*|mexicain\w*|mexikanisch\w*|taquer[ií]a|tacos?)\b/u,
    placeTypes: ["mexican_restaurant"],
    nameHints: ["mexic", "taco", "taqueria", "cantina", "burrito", "guacamole"],
  },
  {
    id: "korean",
    searchTerm: "korean",
    re: /\b(coreano?s?|coreana?s?|korean\w*|cor[eé]en\w*|koreanisch\w*|kimchi|bibimbap)\b/u,
    placeTypes: ["korean_restaurant", "asian_restaurant"],
    nameHints: ["korea", "korean", "coreano", "kimchi", "bibimbap", "seoul"],
  },
  {
    id: "vietnamese",
    searchTerm: "vietnamese",
    re: /\b(vietnamit\w*|vietnames\w*|vietnamien\w*|vietnamesisch\w*|pho\b|banh\s*mi)\b/u,
    placeTypes: ["vietnamese_restaurant", "asian_restaurant"],
    nameHints: ["viet", "pho", "banh mi", "saigon", "hanoi"],
  },
  {
    id: "french",
    searchTerm: "french",
    re: /\b(frances\w*|francesa?s?|french|fran[cç]ais\w*|franz[oö]sisch\w*|bistrot?)\b/u,
    placeTypes: ["french_restaurant"],
    nameHints: ["french", "franc", "bistro", "brasserie", "chez"],
  },
  {
    id: "spanish",
    searchTerm: "spanish",
    re: /\b(espanhol\w*|espanhola?s?|spanish|espa[nñ]ol\w*|spanisch\w*|tapas)\b/u,
    placeTypes: ["spanish_restaurant"],
    nameHints: ["spanish", "espanhol", "tapas", "paella", "iberic"],
  },
  {
    id: "greek",
    searchTerm: "greek",
    re: /\b(greg[oa]s?|grega?s?|greek|grec\w*|griechisch\w*|souvlaki|gyros?)\b/u,
    placeTypes: ["greek_restaurant", "mediterranean_restaurant"],
    nameHints: ["greek", "grego", "souvlaki", "gyros", "athens", "mykonos"],
  },
  {
    id: "turkish",
    searchTerm: "turkish",
    re: /\b(turc[oa]s?|turkish|turque\w*|t[uü]rkisch\w*|kebab)\b/u,
    placeTypes: ["turkish_restaurant", "middle_eastern_restaurant"],
    nameHints: ["turk", "kebab", "istanbul", "anatolia"],
  },
  {
    id: "lebanese",
    searchTerm: "lebanese",
    re: /\b(libanes\w*|lebanese|libanais\w*|libanesisch\w*|shawarma|falafel)\b/u,
    placeTypes: ["lebanese_restaurant", "middle_eastern_restaurant"],
    nameHints: ["liban", "lebanese", "shawarma", "falafel", "beirut"],
  },
  {
    id: "brazilian",
    searchTerm: "brazilian",
    re: /\b(brasileir[oa]s?|brazilian|br[eé]silien\w*|brasilianisch\w*|rodizio|churrascari\w*)\b/u,
    placeTypes: ["brazilian_restaurant", "barbecue_restaurant"],
    nameHints: ["brasil", "brazil", "rodizio", "churrascaria", "feijoada"],
  },
  {
    id: "seafood",
    searchTerm: "seafood",
    re: /\b(marisqueir\w*|marisco?s?|seafood|frutos\s+do\s+mar|fruits\s+de\s+mer|pescado)\b/u,
    placeTypes: ["seafood_restaurant"],
    nameHints: ["marisq", "seafood", "mar", "peixe", "pescador"],
  },
  {
    id: "vegetarian",
    searchTerm: "vegetarian",
    re: /\b(vegetarian\w*|vegetarian[oa]s?|vegan\w*|veget[aá]ri\w*)\b/u,
    placeTypes: ["vegetarian_restaurant", "vegan_restaurant"],
    nameHints: ["veg", "vegan", "vegetarian", "green"],
  },
];

/** "restaurantes X", "comida X", "cozinha X", "X restaurant/food/cuisine". */
const GENERIC_CUISINE = [
  /\b(?:restaurantes?|comida|cozinha|gastronomia)\s+([\p{L}\p{M}-]{4,20})\b/iu,
  /\b(?:restaurants?|food|cuisine)\s+([\p{L}\p{M}-]{4,20})\b/iu,
  /\b([\p{L}\p{M}-]{4,20})\s+(?:restaurants?|cuisine|food)\b/iu,
];

/** Palavras que nunca são cozinha (evita falsos positivos genéricos). */
const NOT_A_CUISINE = new Set([
  "local", "locais", "tipico", "tipicos", "tipica", "tipicas", "tradicional", "tradicionais",
  "bons", "boas", "melhor", "melhores", "bom", "boa", "barato", "baratos", "caro", "caros",
  "perto", "aqui", "onde", "quero", "para", "todos", "todas", "algum", "alguns", "romantic",
  "romantico", "romanticos", "familiar", "familiares", "vegetariana", "best", "good", "cheap",
  "nice", "great", "typical", "traditional", "regional", "regionais", "street", "fast",
  "restaurante", "restaurantes", "restaurant", "restaurants", "cozinha", "comida", "food",
  "cuisine", "mercado", "mercados", "market", "markets",
]);

export function detectCuisineIntent(text: string): CuisineIntent | null {
  const raw = (text ?? "").trim();
  if (!raw) return null;
  const folded = fold(raw);

  for (const entry of CUISINES) {
    if (entry.re.test(folded)) {
      const match = entry.re.exec(folded)?.[0] ?? entry.searchTerm;
      return {
        requested: match,
        id: entry.id,
        searchTerm: entry.searchTerm,
        placeTypes: [...entry.placeTypes],
        nameHints: [...entry.nameHints],
        strict: true,
      };
    }
  }

  // Cozinha fora do catálogo: continua vinculativa, com o termo original.
  for (const re of GENERIC_CUISINE) {
    const hit = re.exec(folded);
    const token = hit?.[1]?.trim();
    if (!token || NOT_A_CUISINE.has(token)) continue;
    return {
      requested: token,
      id: null,
      searchTerm: token,
      placeTypes: [],
      nameHints: [token, token.replace(/(es|as|os|s)$/u, "")],
      strict: true,
    };
  }

  return null;
}

/** Consulta do Places para a cozinha pedida ("japanese restaurants"). */
export function cuisineSearchQuery(intent: CuisineIntent): string {
  return `${intent.searchTerm} restaurants`;
}
